/**
 * The letter a PUBLISHED course grade actually stored, or `null` when there is none.
 *
 * Once a course grade is published, its `grading_scheme_criteria` is the learner's grade:
 * the server resolved it against the WHOLE course, the Course Grades Report and the
 * transcript read it, and it is what the student is shown. The gradebook letter, computed
 * client-side from the RUNNING denominator, answers a different question ("how am I doing
 * on what has been graded so far") and can disagree with it — 36/40 graded in an 80-point
 * course reads A on the running %, while the stored grade is F (CU-868mfkb8m). Every
 * surface that shows a published letter reads it from here so they cannot drift apart.
 *
 * An unpublished grade returns `null` on purpose: until publish there is no final letter,
 * and the callers keep showing the running one with its qualifier.
 *
 * Callers apply their own remediation rule first. A passed remediation keeps two active
 * course grades and is shown as "Pass", which this function does not try to decide.
 *
 * @param {object|null|undefined} courseGrade A course-grade record (`published_at`,
 *   `grading_scheme_criteria_id`, and optionally the loaded `grading_scheme_criteria`).
 * @param {object[]} [criterias] The course's grading-scheme criteria, used to resolve the
 *   stored id when the record arrives without its criterion loaded.
 * @returns {{ name: string, isPassing: boolean|null }|null}
 */
export function resolveStoredCourseLetter(courseGrade, criterias = []) {
    if (!courseGrade?.published_at) return null;

    const criterion = courseGrade.grading_scheme_criteria?.name
        ? courseGrade.grading_scheme_criteria
        : findCriterionById(criterias, courseGrade.grading_scheme_criteria_id);

    if (!criterion?.name) return null;

    return { name: criterion.name, isPassing: toBooleanOrNull(courseGrade.is_passing) };
}

function findCriterionById(criterias, criterionId) {
    if (criterionId === null || criterionId === undefined) return null;

    return (criterias ?? []).find(candidate => Number(candidate?.id) === Number(criterionId)) ?? null;
}

/**
 * `is_passing` is appended by the API only when the record was loaded with
 * `CourseGrade::withIsPassing()`, and may arrive as a boolean or a 0/1 column.
 *
 * @returns {boolean|null}
 */
function toBooleanOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;

    return Number(value) === 1;
}
